import { useEffect, useMemo, useState } from "react"
import { useSearchParams } from "react-router-dom"
import { useFormik } from "formik"
import { toast } from "react-toastify"
import withRouter from "Components/Common/withRouter"
import FormPageTemplate from "Components/templates/form-page-template"
import DepartmentKPIForm from "Components/organisms/forms/department-kpi-form"
import DepartmentKPIFields from "Components/organisms/forms/department-kpi-form/types"
import { DepartmentKPIFormValidation } from "data/department-kpi-form"
import Department from "models/department"
import FiscalYear from "models/fiscal-year"
import Branch from "models/branch"
import { PermissionTypeName } from "models/employee-department-permission"
import {
    getDepartment,
    getDepartmentKPIFormFields,
    submitCreateDepartmentKPIForm
} from "slices/admin/departments/thunk"
import { navigateToDepartmentDetails } from "utils/navigator"

const CreateDepartmentKPIPage = (props: any) => {
    document.title = "Create Department KPIs | IbexIQ";
    const [searchParams] = useSearchParams()
    const departmentId = searchParams.get("departmentId")

    const [isLoading, setIsLoading] = useState<boolean>(true)
    const [department, setDepartment] = useState<Department | null>(null)
    const [fiscalYears, setFiscalYears] = useState<FiscalYear[]>([])
    const [branches, setBranches] = useState<Branch[]>([])

    const initializePage = async () => {
        if (!departmentId) {
            toast.error("Department ID not found")
            props.router.navigate(-1)
            return
        }

        const [dept, fields] = await Promise.all([
            getDepartment(departmentId),
            getDepartmentKPIFormFields()
        ])

        if (!dept) {
            toast.error("Could not find department")
            props.router.navigate(-1)
            return
        }

        // Mirrors the Admin+ check the API enforces on POST /departments/:id/kpis.
        const canManage = !!dept.isGlobalAdmin
            || dept.yourPermission === PermissionTypeName.Owner
            || dept.yourPermission === PermissionTypeName.Admin

        if (!canManage) {
            toast.error("You do not have permission to manage this department's KPIs")
            props.router.navigate(-1)
            return
        }

        setDepartment(dept)
        if (fields) {
            setFiscalYears(fields.fiscalYears)
            setBranches(fields.branches)
        }

        setIsLoading(false)
    }

    useEffect(() => {
        initializePage()
    }, [departmentId])

    const handleBack = () => {
        if (!departmentId) return
        navigateToDepartmentDetails(props.router.navigate, departmentId)
    }

    const onSubmit = async (vals: DepartmentKPIFields) => {
        if (!departmentId) return

        const res = await submitCreateDepartmentKPIForm(departmentId, vals)
        if (res) {
            navigateToDepartmentDetails(props.router.navigate, departmentId)
        }
    }

    const {
        values,
        errors,
        touched,
        handleBlur,
        handleChange,
        handleSubmit,
        isValid,
        isSubmitting
    } = useFormik({
        initialValues: {
            branchId: '',
            fiscalYear: '',
            budget: '',
            annualRevenue: '',
            grossMargin: '',
            workInHand: '',
            performanceManagement: '',
            blueZone: '',
            yellowZone: '',
            opportunityWinTotal: '',
            projectProfitability: '',
            arOverdueTarget: '',
            turnoverTarget: '',
            winRateTarget: ''
        } as DepartmentKPIFields,
        onSubmit,
        validationSchema: DepartmentKPIFormValidation
    })

    const fiscalYearOptions = useMemo(() => fiscalYears.map(f => ({
        value: String(f.id), label: String(f.id)
    })), [fiscalYears])

    const branchOptions = useMemo(() => branches.map(b => ({
        value: b.id, label: b.name
    })), [branches])

    return <FormPageTemplate
        title="Admin"
        breadcrumbItem={department ? `Create KPIs — ${department.code}` : "Create Department KPIs"}
        isLoading={isLoading}
        loadingTitle="Loading Department"
        loadingSubtitle="Fetching department details..."
        form={<DepartmentKPIForm
            fiscalYears={fiscalYearOptions}
            branches={branchOptions}
            values={values}
            errors={errors}
            touched={touched}
            handleChange={handleChange}
            handleBlur={handleBlur}
            handleSubmit={handleSubmit}
            handleCancel={handleBack}
            isValid={isValid}
            isSubmitting={isSubmitting}
        />}
    />
}

export default withRouter(CreateDepartmentKPIPage)
